import React, { useCallback, useEffect, useState } from 'react';
import { useLanguage } from '../context/LanguageContext';

const ACCEPT = 'image/jpeg,image/png,application/pdf';
const MAX_BYTES = 5 * 1024 * 1024;

const cardStyle = {
  background: 'linear-gradient(135deg, #0f2040, #0a1628)',
  border: '1px solid rgba(201,168,76,0.2)',
  borderRadius: '12px',
  padding: '18px',
  marginBottom: '20px',
};

const titleStyle = {
  color: '#c9a84c',
  margin: '0 0 14px',
  fontSize: '14px',
};

function statusBannerStyle(status) {
  const base = {
    borderRadius: '10px',
    padding: '14px 16px',
    marginBottom: '18px',
    fontSize: '13px',
    fontWeight: '600',
    lineHeight: 1.5,
  };
  if (status === 'approved') {
    return { ...base, background: 'rgba(76,175,80,0.12)', border: '1px solid rgba(76,175,80,0.35)', color: '#4CAF50' };
  }
  if (status === 'pending') {
    return { ...base, background: 'rgba(255,152,0,0.12)', border: '1px solid rgba(255,152,0,0.35)', color: '#FF9800' };
  }
  if (status === 'rejected') {
    return { ...base, background: 'rgba(233,69,96,0.12)', border: '1px solid rgba(233,69,96,0.35)', color: '#e94560' };
  }
  return { ...base, background: 'rgba(255,255,255,0.04)', border: '1px solid rgba(255,255,255,0.1)', color: '#8896a8' };
}

function KycFileField({ id, label, icon, file, fileName, onChange, disabled }) {
  return (
    <div style={{ marginBottom: '16px' }}>
      <label htmlFor={id} style={{ display: 'block', color: '#e8e0d0', fontSize: '12px', fontWeight: '600', marginBottom: '8px' }}>
        {icon} {label}
      </label>
      <input
        id={id}
        type="file"
        accept={ACCEPT}
        disabled={disabled}
        onChange={(e) => {
          onChange(e.target.files?.[0] || null);
          e.target.value = '';
        }}
        style={{
          width: '100%',
          fontSize: '12px',
          color: '#8896a8',
          padding: '10px',
          background: 'rgba(0,0,0,0.25)',
          border: '1px solid rgba(201,168,76,0.2)',
          borderRadius: '8px',
        }}
      />
      {fileName ? (
        <p style={{ color: '#c9a84c', fontSize: '11px', margin: '6px 0 0' }}>{fileName}</p>
      ) : null}
    </div>
  );
}

export default function OperatorKycForm({ authToken, apiBaseUrl }) {
  const { t } = useLanguage();
  const [status, setStatus] = useState('not_submitted');
  const [rejectionReason, setRejectionReason] = useState('');
  const [loading, setLoading] = useState(true);
  const [loadError, setLoadError] = useState(null);
  const [submitting, setSubmitting] = useState(false);
  const [submitError, setSubmitError] = useState(null);
  const [submitSuccess, setSubmitSuccess] = useState(false);
  const [license, setLicense] = useState(null);
  const [aadhaarFront, setAadhaarFront] = useState(null);
  const [aadhaarBack, setAadhaarBack] = useState(null);
  const [policeVerification, setPoliceVerification] = useState(null);

  const loadKycStatus = useCallback(async () => {
    if (!authToken) {
      setLoadError(t('sessionExpiredSignIn'));
      setLoading(false);
      return;
    }
    setLoading(true);
    setLoadError(null);
    try {
      const res = await fetch(`${apiBaseUrl}/api/operator/kyc`, {
        headers: { Authorization: `Bearer ${authToken}` },
      });
      const data = await res.json();
      if (!res.ok) {
        throw new Error(data.error || t('failedToLoad'));
      }
      const nextStatus = data.status || data.kyc?.status || 'not_submitted';
      setStatus(nextStatus);
      setRejectionReason(data.kyc?.rejection_reason || '');
    } catch (err) {
      setLoadError(err.message || t('failedToLoad'));
    } finally {
      setLoading(false);
    }
  }, [apiBaseUrl, authToken, t]);

  useEffect(() => {
    loadKycStatus();
  }, [loadKycStatus]);

  const validateFile = (file) => {
    if (!file) return t('genericError');
    if (file.size > MAX_BYTES) return 'File must be under 5 MB';
    const mime = String(file.type || '').toLowerCase();
    if (!ACCEPT.split(',').includes(mime) && !/\.(jpe?g|png|pdf)$/i.test(file.name)) {
      return 'Only JPEG, PNG or PDF files are allowed';
    }
    return null;
  };

  const handleSubmit = async (e) => {
    e.preventDefault();
    setSubmitError(null);
    setSubmitSuccess(false);

    const licenseErr = validateFile(license);
    const frontErr = validateFile(aadhaarFront);
    const backErr = validateFile(aadhaarBack);
    const policeErr = validateFile(policeVerification);
    if (licenseErr || frontErr || backErr || policeErr) {
      setSubmitError(licenseErr || frontErr || backErr || policeErr);
      return;
    }

    setSubmitting(true);
    try {
      const formData = new FormData();
      formData.append('license', license);
      formData.append('aadhaar_front', aadhaarFront);
      formData.append('aadhaar_back', aadhaarBack);
      formData.append('police_verification', policeVerification);

      const res = await fetch(`${apiBaseUrl}/api/operator/kyc/upload`, {
        method: 'POST',
        headers: { Authorization: `Bearer ${authToken}` },
        body: formData,
      });
      const data = await res.json();
      if (!res.ok) {
        throw new Error(data.error || t('genericError'));
      }

      setSubmitSuccess(true);
      setLicense(null);
      setAadhaarFront(null);
      setAadhaarBack(null);
      setPoliceVerification(null);
      await loadKycStatus();
    } catch (err) {
      setSubmitError(err.message || t('genericError'));
    } finally {
      setSubmitting(false);
    }
  };

  const renderStatusLabel = () => {
    if (status === 'approved') return `✅ ${t('kycVerified')}`;
    if (status === 'pending') return t('kycUnderReview');
    if (status === 'rejected') {
      const reason = rejectionReason || '—';
      return `❌ ${t('kycRejected')} — ${reason}`;
    }
    return t('kycNotSubmitted');
  };

  const showUploadForm = status !== 'approved';

  return (
    <div className="w-full min-w-0 overflow-x-hidden">
      <div style={cardStyle}>
        <h3 style={titleStyle}>🪪 {t('kycVerification')}</h3>

        {loading ? (
          <p style={{ color: '#8896a8', fontSize: '13px', margin: 0 }}>{t('loadingLabel')}</p>
        ) : loadError ? (
          <div>
            <p style={{ color: '#e94560', fontSize: '13px', margin: '0 0 12px' }}>{loadError}</p>
            <button
              type="button"
              onClick={loadKycStatus}
              style={{
                background: 'rgba(201,168,76,0.1)',
                border: '1px solid rgba(201,168,76,0.3)',
                color: '#c9a84c',
                padding: '8px 14px',
                borderRadius: '8px',
                cursor: 'pointer',
                fontSize: '12px',
                fontWeight: '600',
              }}
            >
              {t('retryLabel')}
            </button>
          </div>
        ) : (
          <>
            <div style={statusBannerStyle(status)}>
              {renderStatusLabel()}
            </div>

            {submitSuccess ? (
              <p style={{ color: '#4CAF50', fontSize: '12px', margin: '0 0 16px' }}>{t('kycOperatorSubmitSuccess')}</p>
            ) : null}

            {showUploadForm ? (
              <form onSubmit={handleSubmit}>
                <KycFileField
                  id="operator-kyc-license"
                  label={t('kycLicense')}
                  icon="📄"
                  file={license}
                  fileName={license?.name}
                  onChange={setLicense}
                  disabled={submitting}
                />
                <KycFileField
                  id="operator-kyc-aadhaar-front"
                  label={t('kycAadhaarFront')}
                  icon="📄"
                  file={aadhaarFront}
                  fileName={aadhaarFront?.name}
                  onChange={setAadhaarFront}
                  disabled={submitting}
                />
                <KycFileField
                  id="operator-kyc-aadhaar-back"
                  label={t('kycAadhaarBack')}
                  icon="📄"
                  file={aadhaarBack}
                  fileName={aadhaarBack?.name}
                  onChange={setAadhaarBack}
                  disabled={submitting}
                />
                <KycFileField
                  id="operator-kyc-police"
                  label={t('kycPoliceVerification')}
                  icon="📄"
                  file={policeVerification}
                  fileName={policeVerification?.name}
                  onChange={setPoliceVerification}
                  disabled={submitting}
                />

                {submitError ? (
                  <p style={{ color: '#e94560', fontSize: '12px', margin: '0 0 12px' }}>{submitError}</p>
                ) : null}

                <button
                  type="submit"
                  disabled={submitting}
                  style={{
                    width: '100%',
                    padding: '12px',
                    background: submitting ? 'rgba(201,168,76,0.35)' : 'linear-gradient(135deg, #a07830, #e2c97e)',
                    color: '#0a1628',
                    border: 'none',
                    borderRadius: '10px',
                    fontSize: '13px',
                    fontWeight: '800',
                    cursor: submitting ? 'not-allowed' : 'pointer',
                  }}
                >
                  {submitting ? t('processingLabel') : (status === 'rejected' ? t('kycResubmit') : t('kycSubmitBtn'))}
                </button>
              </form>
            ) : null}
          </>
        )}
      </div>
    </div>
  );
}
